import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit2, Trash2, Save, X, Eye, EyeOff } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { useToast } from '../../hooks/use-toast';
import { blogAPI } from '../../utils/api';
import { ImageUploader } from './ImageUploader';

export const BlogManager = () => {
  const { toast } = useToast();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [isAdding, setIsAdding] = useState(false);
  const [newPost, setNewPost] = useState({
    title: '',
    excerpt: '',
    content: '',
    category: '',
    image: '',
    read_time: '5 min read',
    published: true
  });

  const fetchPosts = useCallback(async () => {
    try {
      const response = await blogAPI.getAllAdmin();
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching blog posts:', error);
      toast({
        title: "Error",
        description: "Failed to load blog posts.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const handleAdd = async () => {
    try {
      await blogAPI.create(newPost);
      toast({
        title: "Post Created",
        description: "Blog post has been successfully created.",
      });
      setIsAdding(false);
      setNewPost({
        title: '',
        excerpt: '',
        content: '',
        category: '',
        image: '',
        read_time: '5 min read',
        published: true
      });
      fetchPosts();
    } catch (error) {
      console.error('Error creating post:', error);
      toast({
        title: "Error",
        description: "Failed to create blog post.",
        variant: "destructive"
      });
    }
  };

  const handleEdit = (post) => {
    setEditingId(post.id);
    setEditForm({ ...post });
  };

  const handleSaveEdit = async () => {
    try {
      await blogAPI.update(editingId, editForm);
      toast({
        title: "Post Updated",
        description: "Blog post has been successfully updated.",
      });
      setEditingId(null);
      setEditForm({});
      fetchPosts();
    } catch (error) {
      console.error('Error updating post:', error);
      toast({
        title: "Error",
        description: "Failed to update blog post.",
        variant: "destructive"
      });
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this blog post?')) {
      try {
        await blogAPI.delete(id);
        toast({
          title: "Post Deleted",
          description: "Blog post has been removed.",
        });
        fetchPosts();
      } catch (error) {
        console.error('Error deleting post:', error);
        toast({
          title: "Error",
          description: "Failed to delete blog post.",
          variant: "destructive"
        });
      }
    }
  };

  const handleTogglePublish = async (post) => {
    try {
      await blogAPI.update(post.id, { published: !post.published });
      toast({
        title: post.published ? "Post Unpublished" : "Post Published",
        description: `Blog post has been ${post.published ? 'unpublished' : 'published'}.`,
      });
      fetchPosts();
    } catch (error) {
      console.error('Error toggling publish status:', error);
      toast({
        title: "Error",
        description: "Failed to update publish status.",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading blog posts...</div>;
  }

  return (
    <div className="space-y-6" data-testid="blog-manager">
      {/* Add Button */}
      {!isAdding && (
        <div>
          <Button
            onClick={() => setIsAdding(true)}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="add-blog-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Blog Post
          </Button>
        </div>
      )}

      {/* Add Form */}
      {isAdding && (
        <Card className="border-2 border-emerald-300">
          <CardHeader className="bg-emerald-50">
            <CardTitle>Add New Blog Post</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  value={newPost.title}
                  onChange={(e) => setNewPost({ ...newPost, title: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:border-emerald-500"
                  placeholder="Enter blog title"
                  data-testid="blog-title-input"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Category *
                  </label>
                  <input
                    type="text"
                    value={newPost.category}
                    onChange={(e) => setNewPost({ ...newPost, category: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:border-emerald-500"
                    placeholder="e.g., Lifestyle, Business"
                    data-testid="blog-category-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Read Time
                  </label>
                  <input
                    type="text"
                    value={newPost.read_time}
                    onChange={(e) => setNewPost({ ...newPost, read_time: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:border-emerald-500"
                    placeholder="e.g., 5 min read"
                  />
                </div>
              </div>

              <ImageUploader
                value={newPost.image}
                onChange={(url) => setNewPost({ ...newPost, image: url })}
                label="Featured Image *"
                placeholder="https://images.unsplash.com/..."
              />

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Excerpt *
                </label>
                <textarea
                  value={newPost.excerpt}
                  onChange={(e) => setNewPost({ ...newPost, excerpt: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:border-emerald-500"
                  rows={2}
                  placeholder="Brief summary of the post"
                  data-testid="blog-excerpt-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Content *
                </label>
                <textarea
                  value={newPost.content}
                  onChange={(e) => setNewPost({ ...newPost, content: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:border-emerald-500"
                  rows={6}
                  placeholder="Full blog post content"
                  data-testid="blog-content-input"
                />
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="published-new"
                  checked={newPost.published}
                  onChange={(e) => setNewPost({ ...newPost, published: e.target.checked })}
                  className="w-4 h-4 text-emerald-600 border-slate-300 rounded focus:ring-emerald-500"
                />
                <label htmlFor="published-new" className="text-sm font-medium text-slate-700">
                  Publish immediately
                </label>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  onClick={handleAdd}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="save-blog-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save Post
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsAdding(false);
                    setNewPost({
                      title: '',
                      excerpt: '',
                      content: '',
                      category: '',
                      image: '',
                      read_time: '5 min read',
                      published: true
                    });
                  }}
                  className="border-slate-300"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Posts List */}
      <div className="grid gap-6">
        {posts.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            No blog posts yet. Click "Add Blog Post" to create one.
          </div>
        ) : (
          posts.map((post) => (
            <Card key={post.id} className="border-2" data-testid={`blog-card-${post.id}`}>
              <CardContent className="pt-6">
                {editingId === post.id ? (
                  // Edit Mode
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Title
                      </label>
                      <input
                        type="text"
                        value={editForm.title}
                        onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                        className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:border-emerald-500"
                      />
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Category
                        </label>
                        <input
                          type="text"
                          value={editForm.category}
                          onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                          className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:border-emerald-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Read Time
                        </label>
                        <input
                          type="text"
                          value={editForm.read_time}
                          onChange={(e) => setEditForm({ ...editForm, read_time: e.target.value })}
                          className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:border-emerald-500"
                        />
                      </div>
                    </div>

                    <ImageUploader
                      value={editForm.image}
                      onChange={(url) => setEditForm({ ...editForm, image: url })}
                      label="Featured Image"
                    />

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Excerpt
                      </label>
                      <textarea
                        value={editForm.excerpt}
                        onChange={(e) => setEditForm({ ...editForm, excerpt: e.target.value })}
                        className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:border-emerald-500"
                        rows={2}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Content
                      </label>
                      <textarea
                        value={editForm.content}
                        onChange={(e) => setEditForm({ ...editForm, content: e.target.value })}
                        className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:border-emerald-500"
                        rows={6}
                      />
                    </div>

                    <div className="flex gap-3 pt-4">
                      <Button
                        onClick={handleSaveEdit}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      >
                        <Save className="w-4 h-4 mr-2" />
                        Save Changes
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setEditingId(null);
                          setEditForm({});
                        }}
                        className="border-slate-300"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  // View Mode
                  <div className="flex gap-4">
                    <img 
                      src={post.image} 
                      alt={post.title} 
                      className="w-32 h-32 object-cover rounded-lg flex-shrink-0"
                      onError={(e) => {
                        e.target.src = 'https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?w=400';
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-bold text-xl truncate">{post.title}</h3>
                        {!post.published && (
                          <span className="bg-yellow-100 text-yellow-700 text-xs font-semibold px-2 py-1 rounded flex-shrink-0">
                            Draft
                          </span>
                        )}
                      </div>
                      <p className="text-slate-600 text-sm mb-2 line-clamp-2">{post.excerpt}</p>
                      <div className="flex items-center gap-4 text-sm text-slate-500">
                        <span className="bg-slate-100 px-2 py-0.5 rounded">{post.category}</span>
                        <span>{post.read_time}</span>
                        <span>{new Date(post.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2 flex-shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(post)}
                        data-testid={`edit-blog-${post.id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleTogglePublish(post)}
                        title={post.published ? 'Unpublish' : 'Publish'}
                      >
                        {post.published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-red-300 text-red-600"
                        onClick={() => handleDelete(post.id)}
                        data-testid={`delete-blog-${post.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};
